const PARIS_TIME_ZONE = 'Europe/Paris';

const compactNumberFormatter = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 1,
  notation: 'compact',
});

const quotaDateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  timeZone: PARIS_TIME_ZONE,
  year: 'numeric',
});

const quotaDateTimeFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  month: 'long',
  timeZone: PARIS_TIME_ZONE,
  year: 'numeric',
});

export function formatCompactTokens(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '0';
  }

  return compactNumberFormatter.format(value);
}

export function formatCompactTokensLabel(value: number | null | undefined): string {
  return `≈ ${formatCompactTokens(value)} tokens`;
}

export function formatTokenCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '0';
  }

  return new Intl.NumberFormat('fr-FR').format(value);
}

export function formatSignedTokenDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  if (value === 0) {
    return '0';
  }

  const formatted = formatTokenCount(Math.abs(value));
  return value > 0 ? `+${formatted}` : `−${formatted}`;
}

export function formatQuotaDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return quotaDateFormatter.format(date);
}

export function formatQuotaDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return quotaDateTimeFormatter.format(date);
}

function getParisDateParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    timeZone: PARIS_TIME_ZONE,
    year: 'numeric',
    hour12: false,
  }).formatToParts(date);

  return {
    day: Number(parts.find((part) => part.type === 'day')?.value),
    hour: Number(parts.find((part) => part.type === 'hour')?.value),
    minute: Number(parts.find((part) => part.type === 'minute')?.value),
    month: Number(parts.find((part) => part.type === 'month')?.value),
    year: Number(parts.find((part) => part.type === 'year')?.value),
  };
}

export function calendarDayToParisMidnightUtcIso(year: number, month: number, day: number): string {
  for (let hour = -12; hour < 36; hour += 1) {
    const candidate = new Date(Date.UTC(year, month - 1, day, hour, 0, 0, 0));
    const parts = getParisDateParts(candidate);

    if (parts.year === year && parts.month === month && parts.day === day && parts.hour === 0 && parts.minute === 0) {
      return candidate.toISOString();
    }
  }

  throw new Error('Impossible de convertir la date en minuit Europe/Paris.');
}

export function calendarSelectionToParisMidnightUtcIso(date: Date): string {
  return calendarDayToParisMidnightUtcIso(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

export function isoToParisCalendarDate(iso: string | null | undefined): Date | undefined {
  if (!iso) {
    return undefined;
  }

  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  const parts = getParisDateParts(parsed);
  return new Date(parts.year, parts.month - 1, parts.day);
}

export function getTomorrowParisCalendarDate(reference = new Date()): Date {
  const parts = getParisDateParts(reference);
  const tomorrow = new Date(parts.year, parts.month - 1, parts.day + 1);
  return tomorrow;
}

export function getDefaultQuotaRenewsAtIso(reference = new Date()): string {
  return calendarSelectionToParisMidnightUtcIso(getTomorrowParisCalendarDate(reference));
}

export function getQuotaProgressPercent(consumed: number, allowance: number): number {
  if (allowance <= 0) {
    return consumed > 0 ? 100 : 0;
  }

  return Math.min(100, Math.max(0, (consumed / allowance) * 100));
}

export function getQuotaProgressTone(ratio: number | null | undefined, blocked: boolean) {
  if (blocked || (ratio ?? 0) >= 1) {
    return 'danger' as const;
  }

  if ((ratio ?? 0) >= 0.8) {
    return 'warning' as const;
  }

  return 'success' as const;
}
